<template>
    <section class="px-4 pt-10 pb-5 mx-auto max-w-5xl lg:pt-20 lg:pb-10 xl:px-0">
        <div class="flex flex-wrap gap-y-4 gap-x-12 md:flex-nowrap">
            <div class="order-2 w-full md:order-1">
                <div class="text-sm lg:text-17 sm:text-base font-ww-medium" v-html="introText">
                </div>
            </div>

           <div class="order-1 w-60 lg:w-96 md:w-96 md-max:mx-auto md:order-2">
            <nuxt-img :src="doctorImageSrc"
                 loading="lazy"
                width="238" height="261" sizes="xs:238px sm:23px md:238px lg:238px xl:238px" :alt="doctorImageAlt" format="webp"
                class="mx-auto" />
           </div>
        </div>

        <div class="mt-10 text-sm lg:text-17 sm:text-base font-ww-regular">
            {{ secretText }}

            <ul class="pl-6 mt-6 space-y-1 list-disc">
                <li v-for="(item, index) in secretsList" :key="index" v-html="item.text">
                </li>
            </ul>
        </div>

        <h2 class="mt-12 text-2xl leading-none text-center md:text-3xl lg:text-32 font-ww-bold xl:px-12" v-html="mainHeading">
        </h2>

        <div class="mt-6 text-sm lg:text-17 sm:text-base font-ww-regular">
            <div v-html="engineeringText"></div>
            
            <ul class="pl-6 mt-6 space-y-1 list-disc">
                <li v-for="(item, index) in engineeringList" :key="index" v-html="item.text">
                </li>
            </ul>
        </div>
    </section>
</template>

<script setup>
defineProps({
  introText: {
    type: String,
    default: '<strong>From the desk of Dr. Charles Livingston:</strong><br><br>On July 2nd, 2020, researchers, publishing in Frontiers in Psychology, showed that low doses of caffeine can improve performance on both simple and complex cognitive tasks.<br><br>The study found that caffeine ingestion led to increased activation of the prefrontal cortex associated with executive functions, such as decision-making and attention control.<br><br><strong>Researchers have published hundreds of studies</strong> providing the caffeine\'s brain-enhancing effects.'
  },
  doctorImageSrc: {
    type: String,
    default: '/assets/images/right-section-img.jpg'
  },
  doctorImageAlt: {
    type: String,
    default: 'dr. charles livingston'
  },
  secretText: {
    type: String,
    default: 'But, there\'s a "dirty secret" about caffeine they don\'t want you to know…here\'s what you\'re about to learn:'
  },
  secretsList: {
    type: Array,
    default: () => [
      {
        text: 'Why your <strong>coffee might be killing your productivity</strong> — and how to fix it in 30 seconds each morning.'
      },
      {
        text: 'More caffeine gives you more energy, right? Wrong! <strong>You need to "engineer" your caffeine</strong> for all-day energy.'
      },
      {
        text: '<strong>What never to do after drinking your morning coffee</strong> if you want to avoid the dreaded 2 p.m. crash.'
      },
      {
        text: 'How to experience clean, clear energy without the anxiety, bloating, or crash that comes with most "functional" drinks.'
      },
      {
        text: 'The single most important switch you can make to go from <strong>jittery</strong> and scattered to calm and <strong>focused.</strong>'
      },
      {
        text: 'What Silicon Valley super producers have learned about upgrading their caffeine for <strong>longer-lasting energy without crashes.</strong>'
      }
    ]
  },
  mainHeading: {
    type: String,
    default: '"Secrets of "Caffeine Engineering": How to Get Energy Without the Anxiety, Crash, or Brain Fog'
  },
  engineeringText: {
    type: String,
    default: 'Caffeine has wonderful, brain-boosting benefits. But, piling on more and more caffeine isn\'t the answer to greater energy.<br><br>You need to get more out of smaller doses of caffeine.<br><br>It\'s called "caffeine engineering":'
  },
  engineeringList: {
    type: Array,
    default: () => [
      {
        text: 'Channel the stimulating effects of caffeine into focus, not anxiety.'
      },
      {
        text: 'Use synergistic effects of powerful enhances to extend the energy from caffeine into calm, task-specific focus.'
      },
      {
        text: 'Reduce excessive caffeine\'s downsides: jitteriness, crashes, and brain fog.'
      },
      {
        text: 'Get all the benefits of caffeine while reducing its downsides.'
      }
    ]
  }
})

defineBuilderComponent({
    friendlyName: 'Expert Insight Section',
    canHaveChildren: false,
    inputs: [
        {
            name: 'introText',
            type: 'richText',
            defaultValue: '<strong>From the desk of Dr. Charles Livingston:</strong><br><br>On July 2nd, 2020, researchers, publishing in Frontiers in Psychology, showed that low doses of caffeine can improve performance on both simple and complex cognitive tasks.<br><br>The study found that caffeine ingestion led to increased activation of the prefrontal cortex associated with executive functions, such as decision-making and attention control.<br><br><strong>Researchers have published hundreds of studies</strong> providing the caffeine\'s brain-enhancing effects.'
        },
        {
            name: 'doctorImageSrc',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
            defaultValue: '/assets/images/right-section-img.jpg'
        },
        {
            name: 'doctorImageAlt',
            type: 'text',
            defaultValue: 'dr. charles livingston'
        },
        {
            name: 'secretText',
            type: 'richText',
            defaultValue: 'But, there\'s a "dirty secret" about caffeine they don\'t want you to know…here\'s what you\'re about to learn:'
        },
        {
            name: 'secretsList',
            type: 'list',
            subFields: [
                {
                    name: 'text',
                    type: 'richText',
                    defaultValue: ''
                }
            ],
            defaultValue: [
                {
                    text: 'Why your <strong>coffee might be killing your productivity</strong> — and how to fix it in 30 seconds each morning.'
                },
                {
                    text: 'More caffeine gives you more energy, right? Wrong! <strong>You need to "engineer" your caffeine</strong> for all-day energy.'
                },
                {
                    text: '<strong>What never to do after drinking your morning coffee</strong> if you want to avoid the dreaded 2 p.m. crash.'
                },
                {
                    text: 'How to experience clean, clear energy without the anxiety, bloating, or crash that comes with most "functional" drinks.'
                },
                {
                    text: 'The single most important switch you can make to go from <strong>jittery</strong> and scattered to calm and <strong>focused.</strong>'
                },
                {
                    text: 'What Silicon Valley super producers have learned about upgrading their caffeine for <strong>longer-lasting energy without crashes.</strong>'
                }
            ]
        },
        {
            name: 'mainHeading',
            type: 'richText',
            defaultValue: '"Secrets of "Caffeine Engineering": How to Get Energy Without the Anxiety, Crash, or Brain Fog'
        },
        {
            name: 'engineeringText',
            type: 'richText',
            defaultValue: 'Caffeine has wonderful, brain-boosting benefits. But, piling on more and more caffeine isn\'t the answer to greater energy.<br><br>You need to get more out of smaller doses of caffeine.<br><br>It\'s called "caffeine engineering":'
        },
        {
            name: 'engineeringList',
            type: 'list',
            subFields: [
                {
                    name: 'text',
                    type: 'richText',
                    defaultValue: ''
                }
            ],
            defaultValue: [
                {
                    text: 'Channel the stimulating effects of caffeine into focus, not anxiety.'
                },
                {
                    text: 'Use synergistic effects of powerful enhances to extend the energy from caffeine into calm, task-specific focus.'
                },
                {
                    text: 'Reduce excessive caffeine\'s downsides: jitteriness, crashes, and brain fog.'
                },
                {
                    text: 'Get all the benefits of caffeine while reducing its downsides.'
                }
            ]
        }
    ]
})
</script>